import {Fragment, useState} from 'react'
import {Link, useRouteLoaderData} from '@remix-run/react'
import {Dialog, DialogPanel, Transition, TransitionChild} from '@headlessui/react'
import clsx from 'clsx'
import type {RootLoaderResponse} from '~/types'
import {longWordsLanguages, getTranslation} from '~/localization/translation'
import {BrgIconBurgerMenu} from '~/icons/brg-icon-burger-menu'
import {BrgIconClose} from '~/icons/brg-icon-close'
import {useReportCustomEvent, useReportWidgetClick} from '~/hooks/analytic-hooks'

import {getDomainConfig} from '~/domains'
import SearchBoxRedesign from '../search-combo/search-box-redesign'
import PrimeDayInfo from './PrimeDayInfo'
import BlackFridayInfo from './BlackFridayInfo'
import CyberMondayInfo from './CyberMondayInfo'

const saleEventAppbarInfo = {
  primeDay: {
    logo: <PrimeDayInfo />,
    backgroundColor: 'bg-[#0578FF]',
  },
  blackFriday: {
    logo: <BlackFridayInfo />,
    backgroundColor: 'bg-[#FF6200]',
  },
  cyberMonday: {
    logo: <CyberMondayInfo />,
    backgroundColor: 'bg-[#FF243E]',
  },
} as const

const AppBarAboveTheList = () => {
  const {language, deviceType, featureFlags, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const reportCustomEvent = useReportCustomEvent()
  const reportWidgetClicked = useReportWidgetClick()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [onHoverOnceReportedElements, setOnHoverOnceReportedElements] = useState<string[]>([])
  const {domainName, styles: stylesBydomain, logo} = getDomainConfig(domain, locale)

  const saleEventKey = featureFlags?.enablePrimeDayAppbar
    ? 'primeDay'
    : featureFlags?.enableBlackFridayAppbar
      ? 'blackFriday'
      : featureFlags?.enableCyberMondayAppbar
        ? 'cyberMonday'
        : null

  const saleEventInfo = saleEventKey ? saleEventAppbarInfo[saleEventKey] : null

  const appbarBackground = saleEventInfo
    ? saleEventInfo.backgroundColor
    : `${stylesBydomain.COMMONS_APPBAR_BG} commons_appbar_bg`
  const appbarText = saleEventInfo ? '!text-white' : `${stylesBydomain.COMMONS_APPBAR_TEXT} commons_appbar_text`
  const appbarMenuBurger = saleEventInfo
    ? '[&>.burger-line]:stroke-white'
    : `${stylesBydomain.COMMONS_APPBAR_MENU_BURGER} commons_appbar_menu_burger`
  const appbarMenuCloseButton = saleEventInfo
    ? 'fill-white'
    : `${stylesBydomain.COMMONS_APPBAR_SIDE_MENU_CLOSE_ICON} commons_appbar_side_menu_close_icon`

  return (
    <nav
      className={clsx(
        'sticky top-0 z-50 flex max-w-full justify-center',
        domainName === 'bestreviews' ? '' : 'shadow-md',
        appbarBackground,
        appbarText,
      )}
    >
      <div
        data-testid="app-bar"
        className={clsx(
          'utils_main_container flex w-full flex-row items-center justify-between gap-5 xs-mobile:gap-1',
          featureFlags?.showNewHeaderAndCookieUi ? 'h-16 tablet:h-12' : 'h-16',
          stylesBydomain.UTILS_MAIN_CONTAINER,
        )}
      >
        <Link
          reloadDocument
          data-testid="icon-home-link"
          to="/"
          className="z-40 cursor-pointer"
          aria-label="go home page"
          prefetch="intent"
        >
          {logo}
        </Link>
        {saleEventInfo ? (
          saleEventInfo.logo
        ) : (
          <>
            {(featureFlags?.showNewHeaderAndCookieUi && deviceType !== 'desktop') ||
            domainName !== 'bestreviews' ? null : (
              <span
                className={clsx(
                  "font-semibold text-white after:text-yellow-400 after:content-['.'] tablet:mx-auto mobile:mx-0 mobile:mr-auto mobile:w-44",
                  longWordsLanguages.includes(language) ? 'text-[14px] mobile:text-xs' : 'text-[15px] mobile:text-xs',
                )}
              >
                {translations.AppBar.description}
              </span>
            )}
          </>
        )}

        <Link
          reloadDocument
          to="/about"
          data-testid="about-us-link"
          prefetch="intent"
          aria-label="go to about us page"
          className={clsx(
            'ml-auto block font-semibold tablet:hidden',
            longWordsLanguages.includes(language) ? 'text-[14px]' : 'text-[15px]',
          )}
          onMouseEnter={() => {
            if (!onHoverOnceReportedElements.includes('about') && deviceType === 'desktop') {
              reportCustomEvent('hover', {
                elementName: 'about-us-link',
                placement: 'app-bar',
                variation: 'above-the-list-redesign',
              })
              setOnHoverOnceReportedElements([...onHoverOnceReportedElements, 'about'])
            }
          }}
          onClick={() => {
            reportWidgetClicked({
              widget_name: 'about-us-link',
              widget_place: 'app-bar',
              trigger_type: 'about-us-link',
              variation: 'above-the-list-redesign',
            })
          }}
        >
          {translations.AppBar.about}
        </Link>
        <Link
          reloadDocument
          to="/"
          prefetch="intent"
          aria-label="go to categories page"
          data-testid="categories-link"
          className={clsx(
            'block font-semibold tablet:hidden',
            longWordsLanguages.includes(language) ? 'text-[14px]' : 'text-[15px]',
          )}
          onMouseEnter={() => {
            if (!onHoverOnceReportedElements.includes('categories') && deviceType === 'desktop') {
              reportCustomEvent('hover', {
                elementName: 'categories-link',
                placement: 'app-bar',
                variation: 'above-the-list-redesign',
              })
              setOnHoverOnceReportedElements([...onHoverOnceReportedElements, 'categories'])
            }
          }}
          onClick={() => {
            reportWidgetClicked({
              widget_name: 'categories-link',
              widget_place: 'app-bar',
              trigger_type: 'categories-link',
              variation: 'above-the-list-redesign',
            })
          }}
        >
          {translations.AppBar.categories}
        </Link>

        <div className={clsx('flex w-fit justify-center gap-2', appbarText)}>
          <SearchBoxRedesign isSaleEventInfo={!!saleEventInfo} />
          <button
            aria-label="open mobile menu"
            type="button"
            className="w-full desktop:hidden"
            data-testid="open-menu-mobile"
            onClick={() => {
              setIsMobileMenuOpen(true)
              reportWidgetClicked({
                widget_name: 'search-icon',
                widget_place: 'app-bar',
                trigger_type: 'search-icon',
                variation: 'above-the-list-redesign',
              })
            }}
          >
            <BrgIconBurgerMenu className={appbarMenuBurger} />
          </button>
        </div>
      </div>
      <Transition appear show={isMobileMenuOpen} as={Fragment}>
        <Dialog
          as="div"
          data-testid="mobile-menu"
          className="fixed right-0 top-0 z-50 w-10/12"
          onClose={() => setIsMobileMenuOpen(false)}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </TransitionChild>
          <div className="relative w-full items-center justify-center text-center">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-out duration-100"
              enterFrom="transform translate-x-[200px]"
              enterTo="transform translate-x-0"
              leave="ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogPanel
                className={clsx(
                  'relative h-screen transform overflow-hidden align-middle shadow-xl transition-all',
                  appbarBackground,
                  appbarText,
                )}
              >
                <button
                  aria-label="close mobile menu"
                  data-testid="close-search-mobile"
                  type="button"
                  className="absolute right-10 top-20"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <BrgIconClose className={clsx('h-5 w-5', appbarMenuCloseButton)} />
                </button>
                <div className="mt-36 flex flex-col gap-5">
                  <Link
                    reloadDocument
                    to="/about"
                    prefetch="intent"
                    aria-label="go to about page"
                    className="text-xl font-normal capitalize"
                    onClick={() => {
                      reportWidgetClicked({
                        widget_name: 'about-us-link',
                        widget_place: 'side-bar',
                        trigger_type: 'about-us-link',
                        variation: 'above-the-list-redesign',
                      })
                      setIsMobileMenuOpen(false)
                    }}
                  >
                    {translations.AppBar.about}
                  </Link>
                  <Link
                    reloadDocument
                    to="/"
                    prefetch="intent"
                    aria-label="go to categories page"
                    className="text-xl font-normal capitalize"
                    onClick={() => {
                      reportWidgetClicked({
                        widget_name: 'categories-link',
                        widget_place: 'app-bar',
                        trigger_type: 'categories-link',
                        variation: 'above-the-list-redesign',
                      })
                      setIsMobileMenuOpen(false)
                    }}
                  >
                    {translations.AppBar.categories}
                  </Link>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </nav>
  )
}

export default AppBarAboveTheList
